import { useLocation, useNavigate } from 'react-router-dom';
import {
  IconCheck,
  IconCircleCheckFilled,
  IconCircleXFilled,
  IconHome,
  IconUser,
} from '@tabler/icons-react';
import { Button } from '../../components/common/Button';
import { Card } from '../../components/common/Card';
import { ExamPortalHeader } from './components/ExamPortalHeader';
import classes from './css/ExamSubmittedPage.module.css';
import { getRollNo } from '../../services/authSession';
import type { ExamQuestionOption } from '../../services/sampleExamTypes';

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

type SampleExamQuestionResult = {
  questionId: number;
  questionNo: number;
  questionText: string;
  options: ExamQuestionOption[];
  selectedOptionId: string | null;
  correctOptionId: string | null;
  isCorrect: boolean;
};

type SampleExamResultPayload = {
  totalQuestions: number;
  score: number;
  questionResults: SampleExamQuestionResult[];
};

export interface ExamSubmittedState {
  userName?: string;
  userNameHindi?: string;
  rollNumber?: string;
  submittedAt?: string;
  screenshot?: string;
  result?: SampleExamResultPayload;
}

function formatSubmittedAt(): string {
  const now = new Date();
  const d = now.getDate().toString().padStart(2, '0');
  const m = (now.getMonth() + 1).toString().padStart(2, '0');
  const y = now.getFullYear();
  const h = now.getHours().toString().padStart(2, '0');
  const min = now.getMinutes().toString().padStart(2, '0');
  const s = now.getSeconds().toString().padStart(2, '0');
  return `${d}/${m}/${y} ${h}:${min}:${s}`;
}

export default function ExamSubmittedPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as ExamSubmittedState | null) ?? {};
  const userName = state.userName ?? 'Student';
  const userNameHindi = state.userNameHindi ?? '';
  const rollNumber = getRollNo();
  const result = state.result;

  const getOptionLetter = (options: ExamQuestionOption[], id: string | null) => {
    if (!id) return null;
    const idx = options.findIndex((o) => String(o.optionId) === id);
    return idx >= 0 ? LETTERS[idx] : null;
  };

  return (
    <>
      <ExamPortalHeader />
      <div className={classes.page}>
        <Card className={classes.container} shadow="sm" radius="md">
          {result ? (
            <>
              <div className={classes.resultsHeader}>
                <span className={classes.resultsTitle}>Practice Exam - Results</span>
                <span className={classes.scoreChip}>
                  Score: {result.score}/{result.totalQuestions}
                </span>
              </div>

              <Card className={classes.userCard} shadow="xs" radius="md">
                <div className={classes.userInfoRow}>
                  <div className={classes.avatar}>{String(userName).trim().charAt(0) || 'S'}</div>
                  <div className={classes.userInfoContent}>
                    <div className={classes.userName}>{userName}</div>
                    {userNameHindi ? (
                      <div className={classes.userNameHindi}>{userNameHindi}</div>
                    ) : null}
                    <div className={classes.rollText}>Roll No: {rollNumber}</div>
                  </div>
                </div>
              </Card>

              <Card className={classes.mainScoreCard} shadow="xs" radius="md">
                <div className={classes.mainScoreValue}>
                  {result.score}/{result.totalQuestions}
                </div>
                <div className={classes.mainScoreText}>
                  You got {result.score} out of {result.totalQuestions} questions correct
                </div>
              </Card>

              <div className={classes.resultList}>
                {result.questionResults.map((q) => (
                  <Card
                    key={q.questionId}
                    className={`${classes.questionCard} ${q.isCorrect ? classes.cardCorrect : classes.cardWrong}`}
                    shadow="xs"
                    radius="md"
                  >
                    <div className={classes.questionTitleRow}>
                      {q.isCorrect ? (
                        <IconCircleCheckFilled
                          size={18}
                          className={classes.correctIcon}
                          aria-hidden
                        />
                      ) : (
                        <IconCircleXFilled size={18} className={classes.wrongIcon} aria-hidden />
                      )}
                      <div className={classes.questionText}>
                        Q{q.questionNo}. {q.questionText}
                      </div>
                    </div>

                    <div className={classes.optionsGrid}>
                      {q.options.map((opt, idx) => {
                        const optionId = String(opt.optionId);
                        const isSelected = q.selectedOptionId === optionId;
                        const isCorrect = q.correctOptionId === optionId;
                        const className = `${classes.optionRow} ${
                          isCorrect
                            ? classes.optionCorrect
                            : isSelected && !q.isCorrect
                              ? classes.optionWrong
                              : ''
                        }`;
                        return (
                          <div key={optionId} className={className}>
                            <span className={classes.optionLetter}>{LETTERS[idx]}</span>
                            <span>{opt.optionText}</span>
                          </div>
                        );
                      })}
                    </div>

                    {!q.isCorrect && q.correctOptionId ? (
                      <div className={classes.correctAnswerText}>
                        Correct Answer:{' '}
                        {(() => {
                          const correctLetter = getOptionLetter(q.options, q.correctOptionId);
                          const correctText =
                            q.options.find((o) => String(o.optionId) === q.correctOptionId)
                              ?.optionText ?? '';
                          return correctLetter ? `${correctLetter}. ${correctText}` : correctText;
                        })()}
                      </div>
                    ) : null}
                  </Card>
                ))}
              </div>

              <Button className={classes.dashboardBtn} onClick={() => navigate('/dashboard')}>
                <IconHome size={20} aria-hidden />
                Go to Dashboard
              </Button>
            </>
          ) : (
            <>
              <div className={classes.successIconWrap}>
                <IconCheck size={48} strokeWidth={3} aria-hidden />
              </div>
              <h1 className={classes.title}>Exam Submitted Successfully!</h1>

              <Card className={classes.detailsCard} shadow="sm" radius="md">
                <div className={classes.cardRow}>
                  <IconUser size={20} className={classes.cardIcon} aria-hidden />
                  <div className={classes.cardContent}>
                    <span className={classes.cardLabel}>Name</span>
                    <span className={classes.cardValue}>{userName}</span>
                    {userNameHindi && (
                      <span className={classes.cardValueHindi}>{userNameHindi}</span>
                    )}
                  </div>
                </div>
                <div className={classes.cardDivider} />
                <div className={classes.cardRow}>
                  <div className={classes.cardContent}>
                    <span className={classes.cardLabel}>Roll Number</span>
                    <span className={classes.cardValue}>{rollNumber}</span>
                  </div>
                </div>
              </Card>

              <p className={classes.confirmText}>Your exam has been submitted successfully.</p>
              <p className={classes.resultsText}>
                Submitted at: {state.submittedAt ?? formatSubmittedAt()}
              </p>
              <Button
                className={classes.dashboardBtn}
                onClick={() => navigate('/dashboard')}
                aria-label="Go to Dashboard"
              >
                <IconHome size={22} aria-hidden />
                Go to Dashboard
              </Button>
            </>
          )}
        </Card>
      </div>
    </>
  );
}
